import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import pDebounce from 'p-debounce'
import { useTranslation } from 'react-i18next'
import Popper from '@mui/material/Popper'
import { isEmpty } from 'lodash'
import MuiAutoComplete, {
  CATEGORY,
} from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/mui-autocomplete.component'
import {
  useDefaultOptionsWithoutRecentAddress,
  buildAddressBookOptions,
  buildRecentAddressOptions,
  FIELD_NAME,
} from './utils'

const PhoneNumberAutocomplete = ({
  className,
  phone_number = {},
  address = {},
  latitude = {},
  longitude = {},
  dataTestid,
  placeholder,
  onChange,
  onOpenAddressBookModal,
  onInputChange,
  onBlur,
  onHover,
  isAddressBookOptionsReady,
}) => {
  const [t] = useTranslation()
  const { touched, dirty, error } = phone_number.meta || {}
  const { value: phoneNumberValue } = phone_number.input || {}
  const autocompleteValue = { label: phoneNumberValue, value: phoneNumberValue }

  const handleAutocompleteQuery = useMemo(() => {
    const debouncedPreparedOptions = pDebounce(
      value =>
        buildAddressBookOptions(t)(FIELD_NAME.PHONE_NUMBER, value).catch(
          err => {
            console.warn(
              'NameAutocomplete getContacts failed => searchText: ',
              value
            )
            console.warn('NameAutocomplete getContacts failed => err: ', err)
          }
        ),
      500
    )
    return async value => await debouncedPreparedOptions(value)
  }, [t])

  const handleAutocompleteChange = useCallback(
    async (valueObj = {}) => {
      if (!address?.input || !latitude?.input || !longitude?.input) return

      if (isEmpty(valueObj))
        return onChange({ address: null, latitude: null, longitude: null })

      const value =
        (valueObj.groupType === 'recent-address' && valueObj) ||
        (valueObj.groupType === 'saved-address-book' && valueObj.value) ||
        null
      if (value) {
        onChange({
          address: value.address,
          latitude: value.latitude.toString(),
          longitude: value.longitude.toString(),
          details: value.detail_address,
          phone_number: value.contact_phone_number,
          name: value.contact_name,
          saved_address_id: value.saved_address_id,
        })
      }
    },
    [address, latitude, longitude]
  )

  const defaultOptions = useDefaultOptionsWithoutRecentAddress(
    FIELD_NAME.PHONE_NUMBER,
    phoneNumberValue,
    isAddressBookOptionsReady
  )

  return (
    <MuiAutoComplete
      className={className}
      category={CATEGORY.GENERIC}
      onInputChange={value => {
        onInputChange()
        phone_number.input?.onChange(value)
      }}
      onBlur={() => {
        onBlur()
        phone_number.input?.onBlur(phoneNumberValue)
      }}
      onHover={onHover}
      onQuery={handleAutocompleteQuery}
      onChange={handleAutocompleteChange}
      defaultOptions={defaultOptions}
      menuHeight={300}
      meta={{ touched: touched || dirty, error }}
      showSaveAddressModal={onOpenAddressBookModal}
      selectedOption={autocompleteValue}
      supportEditSelectedValue
      placeholder={placeholder}
      dataTestid={dataTestid}
      customPopperComponent={({ style, ...props }) => (
        <Popper style={{ width: '453px' }} {...props} placement="bottom-end" />
      )}
    />
  )
}

const reduxFormPropTypeShape = PropTypes.shape({
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
})

PhoneNumberAutocomplete.propTypes = {
  className: PropTypes.string,
  phone_number: reduxFormPropTypeShape,
  address: reduxFormPropTypeShape,
  latitude: reduxFormPropTypeShape,
  longitude: reduxFormPropTypeShape,
  dataTestid: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onOpenAddressBookModal: PropTypes.func,
  onInputChange: PropTypes.func,
  onBlur: PropTypes.func,
  onHover: PropTypes.func,
  isAddressBookOptionsReady: PropTypes.bool,
}

export { PhoneNumberAutocomplete }
